import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { AdminUser } from '../data/types';
import { auth, isOtpRequired, setAccessToken } from '../data/live';

type Theme = 'light' | 'dark' | 'color' | 'scifi';

interface Session {
  user: AdminUser | null;
  /** Screens the *server* says this account may reach. The nav uses this rather
   *  than a table compiled into the bundle, so a role change takes effect on
   *  the next refresh instead of the next deploy. */
  screens: string[];
  /** True while the initial refresh is in flight — before it settles we do not
   *  know whether there is a session, and rendering the login form during that
   *  window flashes it at everyone who is already signed in. */
  loading: boolean;
  mustChangePassword: boolean;
  /** Resolves to the number of minutes a code is good for when the console is
   *  set to ask for one, and to null when the password alone signed you in. */
  signIn: (email: string, password: string) => Promise<number | null>;
  /** Finishes a sign-in that returned a code challenge. */
  completeOtp: (email: string, code: string) => Promise<void>;
  signOut: () => void;
  changePassword: (current: string, next: string) => Promise<void>;
  theme: Theme;
  setTheme: (t: Theme) => void;
}

const Ctx = createContext<Session | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [screens, setScreens] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [mustChange, setMustChange] = useState(false);

  const [theme, setThemeState] = useState<Theme>(() => {
    try {
      const v = localStorage.getItem('sparkev-admin-theme');
      if (v === 'light' || v === 'dark' || v === 'color' || v === 'scifi') return v;
    } catch { /* private mode, or storage disabled */ }
    return 'light';
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try { localStorage.setItem('sparkev-admin-theme', theme); } catch { /* ignore */ }
  }, [theme]);

  const adopt = useCallback((r: {
    accessToken: string;
    user: { sub: string; email: string; name: string; role: AdminUser['role']; mustChangePassword: boolean };
    screens: string[];
  }) => {
    /* The access token is held in memory and nowhere else. In web storage it
       would be readable by any script that ever gets injected; here it dies
       with the tab, and the httpOnly refresh cookie brings it back. */
    setAccessToken(r.accessToken);
    setUser({ name: r.user.name, email: r.user.email, role: r.user.role });
    setScreens(r.screens);
    setMustChange(r.user.mustChangePassword);
  }, []);

  /* On load, try to trade the refresh cookie for an access token. A 401 here is
     the normal "not signed in" case, not an error worth showing. */
  useEffect(() => {
    let live = true;
    auth.refresh()
      .then((r) => { if (live) adopt(r); })
      .catch(() => { /* no session */ })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [adopt]);

  /* The access token expires well before the refresh cookie does, so renew it
     on a timer rather than waiting for a request to fail and retrying. */
  useEffect(() => {
    if (!user) return;
    const t = setInterval(() => {
      auth.refresh().then(adopt).catch(() => {
        setAccessToken(null);
        setUser(null);
      });
    }, 15 * 60_000);
    return () => clearInterval(t);
  }, [user, adopt]);

  const value = useMemo<Session>(() => ({
    user,
    screens,
    loading,
    mustChangePassword: mustChange,
    theme,
    setTheme: setThemeState,
    signIn: async (email, password) => {
      const r = await auth.login(email, password);
      if (isOtpRequired(r)) return r.expiresInMinutes;
      adopt(r);
      return null;
    },
    completeOtp: async (email, code) => adopt(await auth.otp(email, code)),
    signOut: () => {
      void auth.logout().catch(() => { /* the cookie is cleared either way */ });
      setAccessToken(null);
      setUser(null);
      setScreens([]);
      setMustChange(false);
    },
    changePassword: async (current, next) => {
      await auth.changePassword(current, next);
      // Every session ended, including this one — sign in again with the new
      // password rather than pretending the old token still means anything.
      setAccessToken(null);
      setUser(null);
      setMustChange(false);
    },
  }), [user, screens, loading, mustChange, theme, adopt]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): Session {
  const c = useContext(Ctx);
  if (!c) throw new Error('useSession outside SessionProvider');
  return c;
}
